import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import api from '../services/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Bell, 
  Mail, 
  Clock, 
  CheckCircle, 
  PlusCircle,
  Save,
  ArrowLeft
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export const TaskNotificationAdminPage = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState({
    notify_on_create: true,
    notify_on_complete: true,
    reminder_enabled: true,
    reminder_days_before: 1,
    notify_creator: true,
    notify_assignees: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/task-notification-settings');
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
      // Use defaults if not found
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await api.put('/task-notification-settings', settings);
      toast.success('Nastavení uloženo');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Chyba při ukládání nastavení');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-64">
          <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 max-w-3xl mx-auto">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/admin')}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Zpět na administraci
        </Button>

        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Bell className="w-7 h-7 text-blue-600" />
            Nastavení notifikací úkolů
          </h1>
          <p className="text-slate-500 mt-1">
            Konfigurace emailových notifikací pro modul úkolů
          </p>
        </div>

        <div className="space-y-6">
          {/* Notification Events */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5" />
                Události k notifikaci
              </CardTitle>
              <CardDescription>
                Vyberte, při kterých událostech se mají posílat emailové notifikace
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* On Create */}
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <div className="flex items-center gap-2">
                    <PlusCircle className="w-4 h-4 text-blue-500" />
                    <Label className="text-base">Při vytvoření úkolu</Label>
                  </div>
                  <p className="text-sm text-slate-500">
                    Odeslat email při přiřazení nového úkolu
                  </p>
                </div>
                <Switch
                  checked={settings.notify_on_create}
                  onCheckedChange={(checked) => setSettings({ ...settings, notify_on_create: checked })}
                />
              </div>

              {/* On Complete */}
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <Label className="text-base">Při splnění úkolu</Label>
                  </div>
                  <p className="text-sm text-slate-500">
                    Odeslat email při označení úkolu jako splněného
                  </p>
                </div>
                <Switch
                  checked={settings.notify_on_complete}
                  onCheckedChange={(checked) => setSettings({ ...settings, notify_on_complete: checked })}
                />
              </div>

              {/* Reminder */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-orange-500" />
                      <Label className="text-base">Připomínky před termínem</Label>
                    </div>
                    <p className="text-sm text-slate-500">
                      Odeslat připomínku před blížícím se termínem úkolu
                    </p>
                  </div>
                  <Switch
                    checked={settings.reminder_enabled}
                    onCheckedChange={(checked) => setSettings({ ...settings, reminder_enabled: checked })}
                  />
                </div>

                {settings.reminder_enabled && (
                  <div className="ml-6 pl-4 border-l-2 border-slate-200">
                    <Label className="text-sm text-slate-600">Kolik dní před termínem</Label>
                    <Select
                      value={String(settings.reminder_days_before)}
                      onValueChange={(value) => setSettings({ ...settings, reminder_days_before: parseInt(value) })}
                    >
                      <SelectTrigger className="w-48 mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">1 den</SelectItem>
                        <SelectItem value="2">2 dny</SelectItem>
                        <SelectItem value="3">3 dny</SelectItem>
                        <SelectItem value="5">5 dní</SelectItem>
                        <SelectItem value="7">7 dní</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Recipients */}
          <Card>
            <CardHeader>
              <CardTitle>Příjemci notifikací</CardTitle>
              <CardDescription>
                Komu se mají posílat emailové notifikace
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="text-base">Zadavatel úkolu</Label>
                  <p className="text-sm text-slate-500">
                    Osoba, která úkol vytvořila
                  </p>
                </div>
                <Switch
                  checked={settings.notify_creator}
                  onCheckedChange={(checked) => setSettings({ ...settings, notify_creator: checked })}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="text-base">Řešitelé úkolu</Label>
                  <p className="text-sm text-slate-500">
                    Osoby přiřazené k úkolu
                  </p>
                </div>
                <Switch
                  checked={settings.notify_assignees}
                  onCheckedChange={(checked) => setSettings({ ...settings, notify_assignees: checked })}
                />
              </div>
            </CardContent>
          </Card>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button 
              onClick={handleSave}
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Ukládání...' : 'Uložit nastavení'}
            </Button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default TaskNotificationAdminPage;
